import Button from "@common/components/Button";
import Dialog from "@common/components/Dialog";
import DialogActions from "@common/components/Dialog/DialogActions";
import DialogTitle from "@common/components/Dialog/DialogTitle";
import {
  Accordion,
  DialogContent,
  DialogContentText,
  Stack,
  Switch,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserSettings } from "../../SettingsContent";
import { CookieCategory } from "./type";
import AccordionSummary from "@common/components/Accordion/AccordionSummary";
import Typography from "@common/components/Typography";
import AccordionDetails from "@common/components/Accordion/AccordionDetails";
import PropertyItem from "@common/components/PropertyItem";
import { GOOGLE_ANALYTICS_COOKIES } from "../GoogleAnalytics/const";

type AdvancedCookiesDialogProps = {
  open: boolean;
  onClose: (value: boolean) => void;
};

const AdvancedCookiesDialog = (props: AdvancedCookiesDialogProps) => {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const [userSettings, setUserSettings] = useUserSettings();
  const { areAnalyticsActive } = userSettings;
  const essentialCookies: CookieCategory = {
    title: t("Essential"),
    active: true,
    description: t(
      "These cookies are required in order for users to navigate Symphony and use its features, which include, inter alia, access to secure area without having to repeatedly log in and remembering your actions in each page during one session..."
    ),
    required: true,
  };
  const [googleAnalytics, setGoogleAnalytics] = useState<boolean | undefined>(
    areAnalyticsActive
  );
  const googleAnalyticsCookies: CookieCategory = {
    title: t("Performance and Analytics"),
    active: googleAnalytics ?? false,
    description: t(
      "Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc."
    ),
    onClick: (value: boolean) => setGoogleAnalytics(value),
    cookies: GOOGLE_ANALYTICS_COOKIES,
    required: false,
  };
  const categories = [essentialCookies, googleAnalyticsCookies];

  const handleOnSave = () => {
    setUserSettings({ ...userSettings, areAnalyticsActive: googleAnalytics });
    onClose(false);
  };
  const handleOnClose = () => {
    if (areAnalyticsActive !== undefined) onClose(false);
  };
  const changeAllCategories = (value: boolean) => {
    categories.forEach((category) => {
      if (category.onClick) category.onClick(value);
    });
  };

  useEffect(() => {
    if (open) setGoogleAnalytics(areAnalyticsActive);
  }, [areAnalyticsActive, open]);

  return (
    <Dialog open={open} onClose={handleOnClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("Advanced Cookies Option")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("We use some essential cookies to make this service work.")}
        </DialogContentText>
        <DialogContentText>
          {t(
            "We'd also like to use analytics cookies so we can understand how you use the service and make improvements."
          )}
        </DialogContentText>
        <Stack sx={{ marginTop: 3 }}>
          {categories.map((category, index) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: ({ palette }) => palette.background.default,
              }}
            >
              <AccordionSummary>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center"
                  }}>
                  {category.title}
                  {category.required ? (
                    <Typography>{t("Always Active")}</Typography>
                  ) : (
                    <Switch
                      checked={category.active}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        if (category.onClick) {
                          category.onClick(event.target.checked);
                        }
                      }}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="column" sx={{
                  gap: 3
                }}>
                  <Typography>{category.description}</Typography>
                  {category.cookies?.map((cookie, index) => (
                    <Stack key={index} sx={{
                      gap: 1
                    }}>
                      <PropertyItem name={t("Cookies")}>
                        {cookie.name}
                      </PropertyItem>
                      <PropertyItem name={t("Description")}>
                        {t(cookie.description)}
                      </PropertyItem>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="white"
          disabled={googleAnalytics === undefined}
          onClick={handleOnSave}
        >
          Save preferences
        </Button>
        <Button variant="outlined" onClick={() => changeAllCategories(true)}>
          Accept all
        </Button>
        <Button variant="outlined" onClick={() => changeAllCategories(false)}>
          Reject all
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedCookiesDialog;
